import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import { Maybe, ParkZone } from 'types/generatedSchemaTypes';

/**
 * Whether a zone only offers charging, seen from Aimo's side: it has EV
 * charging and there is no active short-term parking tariff for it.
 *
 * `parkingMethods.unmanagedParking` is not that signal: it says the parking at
 * the zone is run by another company, and nothing about charging. Zone 3929
 * example that broke — the field is not set at all,
 * yet its short-term pricing scheme carries no tariff plans, so no
 * parking session can be started there.
 *
 * `ChargerListing` reads this hook, but the flag is still used as a
 * charging-only stand-in elsewhere (`useTariffs`, `OngoingParkingZoneDetails`,
 * the `directToChargeSelect` shortcut in `SearchItem`/`AimoMarker`, and marker
 * appearance in `SelectedMarker`/`MapDataRenderers`). Prefer this hook when
 * touching those. The map and search rows are the awkward ones: they decide
 * per list row, and neither `parkZonesV2` nor `parkZone` returns any tariff
 * information, so answering there costs a query per zone.
 *
 * ANPR zones are never charging-only: Aimo manages their parking through the
 * cameras rather than an app-started session, and `useParkingPricingInfo` skips
 * the tariff query for them altogether, so their missing `pricingSchemeId`
 * says nothing about whether parking exists.
 *
 * The answer is `false` while the tariff query is loading — a zone must
 * not be committed to a charging-only flow before pricing resolved.
 * Callers that need to wait rather than assume can read
 * `isResolvingStpPricing`.
 */
const useIsEvChargingOnlyZone = (parkZone?: Maybe<ParkZone>) => {
  const { uid, parkingMethods } = parkZone ?? {};
  const isAnpr = parkingMethods?.anpr === true;
  const { pricingSchemeId, pricingInfoLoading } = useParkingPricingInfo(
    uid ?? '',
    true,
    isAnpr
  );
  const hasActiveStpPricing = !!pricingSchemeId;

  return {
    isEvChargingOnlyZone:
      !!parkingMethods?.evCharging &&
      !isAnpr &&
      !pricingInfoLoading &&
      !hasActiveStpPricing,
    isResolvingStpPricing: pricingInfoLoading,
    hasActiveStpPricing,
  };
};

export default useIsEvChargingOnlyZone;
